import {
    CameraOutlined,
    DeleteOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { App, Button, Flex, Upload, type UploadProps } from "antd";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
    getApiError,
    patchUserUpdateThunk,
    postFileUpload,
} from "tm-shared/api";
import { createUniqueId, readFileAsBase64 } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BaseModal } from "@/shared/ui/BaseModal";
import { SecondaryText } from "@/shared/ui/Typography";
import { getFileUrl } from "@/utils";

import {
    StyledEmptyPreview,
    StyledPreviewBadge,
    StyledPreviewCard,
    StyledPreviewImage,
} from "./Styled";

interface ChangePhotoModalProps {
    open: boolean;
    onClose: () => void;
}

const MAX_FILE_SIZE_MB = 5;

export const ChangePhotoModal: FC<ChangePhotoModalProps> = ({
    open,
    onClose,
}) => {
    const { notification } = App.useApp();

    const {
        firstName,
        lastName,
        photo,
        id: userId,
        isExternal,
    } = useAppSelector((s) => s.user);

    const currentPhoto = photo ? getFileUrl(photo) : null;

    const dispatch = useAppDispatch();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(currentPhoto);
    const [shouldDelete, setShouldDelete] = useState(false);
    const [loading, setLoading] = useState(false);
    const objectUrlRef = useRef<string | null>(null);

    const revokeObjectUrl = useCallback(() => {
        if (!objectUrlRef.current) return;

        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
    }, []);

    useEffect(() => {
        if (!open) return;

        revokeObjectUrl();
        setSelectedFile(null);
        setPreviewUrl(currentPhoto);
        setShouldDelete(false);
        setLoading(false);
    }, [currentPhoto, open, revokeObjectUrl]);

    useEffect(() => {
        return () => {
            revokeObjectUrl();
        };
    }, [revokeObjectUrl]);

    const resetLocalState = useCallback(() => {
        revokeObjectUrl();
        setSelectedFile(null);
        setPreviewUrl(currentPhoto);
        setShouldDelete(false);
        setLoading(false);
    }, [currentPhoto, revokeObjectUrl]);

    const handleFileChange: UploadProps["beforeUpload"] = (file) => {
        const isFileSizeValid = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;

        if (!isFileSizeValid) {
            notification.error({
                title: "Файл завеликий",
                description: `Розмір файлу не може перевищувати ${MAX_FILE_SIZE_MB} МБ.`,
            });
            return false;
        }

        revokeObjectUrl();

        const url = URL.createObjectURL(file);

        objectUrlRef.current = url;
        setSelectedFile(file);
        setPreviewUrl(url);
        setShouldDelete(false);

        return false;
    };

    const handleSecondaryAction = () => {
        if (selectedFile) {
            revokeObjectUrl();
            setSelectedFile(null);
            setPreviewUrl(currentPhoto);
            setShouldDelete(false);
            return;
        }

        if (shouldDelete) {
            setPreviewUrl(currentPhoto);
            setShouldDelete(false);
            return;
        }

        setPreviewUrl(null);
        setShouldDelete(true);
    };

    const handleClose = () => {
        resetLocalState();
        onClose();
    };

    const hasChanges = shouldDelete || !!selectedFile;
    const showSecondaryAction =
        !!currentPhoto || !!previewUrl || !!selectedFile;

    const statusTone = shouldDelete
        ? "warning"
        : selectedFile
          ? "success"
          : "default";

    const secondaryActionLabel = selectedFile
        ? "Скасувати вибір"
        : shouldDelete
          ? "Повернути фото"
          : "Видалити фото";

    const handleSave = async () => {
        if (!hasChanges) return;

        setLoading(true);

        try {
            const base64Content = selectedFile
                ? await readFileAsBase64(selectedFile)
                : null;

            let newPhotoUrl: string | null = null;

            if (base64Content && selectedFile) {
                const extension = selectedFile.name.includes(".")
                    ? selectedFile.name.split(".").pop()?.toLowerCase()
                    : undefined;

                const { result } = await postFileUpload({
                    space: userId,
                    filename: `${createUniqueId()}${
                        extension ? `.${extension}` : ""
                    }`,
                    base64Content,
                });

                newPhotoUrl = result;
            }

            await dispatch(
                patchUserUpdateThunk({
                    userId,
                    lastName,
                    firstName,
                    isExternal,
                    photo: newPhotoUrl,
                }),
            );

            resetLocalState();
            onClose();
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Не вдалося оновити фото",
                description: displayMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <BaseModal
            open={open}
            title="Фото профілю"
            onCancel={handleClose}
            onClose={handleClose}
            onConfirm={handleSave}
            confirmText="Зберегти зміни"
            confirmLoading={loading}
            confirmDisabled={!hasChanges}
            cancelText="Скасувати"
        >
            <Flex vertical gap={12}>
                <Upload
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    showUploadList={false}
                    beforeUpload={handleFileChange}
                    disabled={loading}
                    maxCount={1}
                    style={{ display: "block" }}
                >
                    <StyledPreviewCard $tone={statusTone}>
                        {previewUrl ? (
                            <StyledPreviewImage
                                src={previewUrl}
                                alt="Попередній перегляд фото профілю"
                            />
                        ) : (
                            <StyledEmptyPreview>
                                <UserOutlined />
                            </StyledEmptyPreview>
                        )}

                        <StyledPreviewBadge>
                            <CameraOutlined />
                        </StyledPreviewBadge>
                    </StyledPreviewCard>
                </Upload>

                <Flex vertical gap={8}>
                    {showSecondaryAction && (
                        <Button
                            icon={<DeleteOutlined />}
                            block
                            danger={!selectedFile}
                            size="large"
                            onClick={handleSecondaryAction}
                            disabled={loading}
                        >
                            {secondaryActionLabel}
                        </Button>
                    )}
                </Flex>

                <SecondaryText fontSize={12} textAlign="center">
                    Підтримуються JPG та PNG до {MAX_FILE_SIZE_MB} МБ.
                </SecondaryText>
            </Flex>
        </BaseModal>
    );
};
